import { Link } from "react-router-dom";
import { MessageSquareText, ShieldCheck, Smartphone, Building2 } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { useT } from "@/lib/i18n";

const SAMPLE = "PhonePlay: Your verification code is 123456. Reply STOP to opt out, HELP for help.";

function ConsentDemo({ t, id }) {
  return (
    <div className="rounded-xl bg-black/40 border border-white/10 p-4 max-w-md">
      <input type="tel" readOnly value="+1" aria-label="Mobile number (example)" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 font-mono text-gray-400 mb-3" />
      <label className="flex items-start gap-2.5 text-[11px] leading-relaxed text-gray-400">
        <input data-testid={`${id}-checkbox`} type="checkbox" defaultChecked={false} className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-black/40 accent-cyan-500" />
        <span data-testid={`${id}-text`}>{t("sms_consent")}{" "}
          <Link to="/terms" className="text-cyan-400 hover:underline">Terms &amp; Rules</Link> · <Link to="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</Link>
        </span>
      </label>
      <button type="button" disabled className="mt-3 px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-600 font-semibold opacity-40 cursor-not-allowed">Send code</button>
      <p className="text-[10px] text-gray-600 mt-2">The checkbox is unchecked by default; "Send code" stays disabled until it is checked.</p>
    </div>
  );
}

export default function SmsOptIn() {
  const { t } = useT();
  useSeo({
    title: "PhonePlay SMS Opt-In",
    path: "/sms-opt-in",
    description: "How PhonePlay collects consent for account and security text messages: optional opt-in at signup or in Wallet, exact consent language, sample message, and STOP/HELP instructions.",
  });
  return (
    <article data-testid="sms-opt-in-page" className="space-y-10">
      <header>
        <h1 className="font-display text-3xl sm:text-4xl font-black mb-3">PhonePlay SMS Opt-In</h1>
        <p className="text-gray-400 leading-relaxed">
          This page documents how PhonePlay obtains consent for text messages. SMS is <span className="text-gray-200 font-semibold">optional</span> and
          used <span className="text-gray-200 font-semibold">only for account security</span>: one-time passcodes, login verification and account
          activity alerts. We never send marketing or promotional texts, and a phone number is never required to play.
        </p>
      </header>

      <section className="glass rounded-2xl p-6 border border-white/10" data-testid="sms-business">
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><Building2 className="w-5 h-5 text-amber-400" /> Business</h2>
        <p className="text-gray-300">Phone Play, LLC</p>
        <p className="text-gray-400 text-sm">5675 Jimmy Carter Blvd, Suite 725, Norcross, GA 30071</p>
        <p className="text-gray-400 text-sm">Website: <a href="https://phoneplay.net" className="text-cyan-400 hover:underline">https://phoneplay.net</a> · Email: <a href="mailto:support@phoneplay.net" className="text-cyan-400 hover:underline">support@phoneplay.net</a></p>
        <p className="text-gray-500 text-xs mt-2">Program: PhonePlay account &amp; security notifications (transactional only).</p>
      </section>

      <section data-testid="sms-paths">
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><Smartphone className="w-5 h-5 text-cyan-400" /> How users opt in</h2>
        <ol className="list-decimal list-inside space-y-2 text-gray-400 text-sm">
          <li><span className="text-gray-200">Signup</span> at <a href="https://phoneplay.net" className="text-cyan-400 hover:underline">https://phoneplay.net</a> — the mobile number field is optional; the user must tick the consent checkbox before a code can be sent.</li>
          <li><span className="text-gray-200">Wallet › Phone &amp; Two-Factor Security</span> after login — the user enters a number, ticks the same consent checkbox, then presses "Send code".</li>
        </ol>
        <p className="text-gray-500 text-xs mt-2">Consent is collected only through these two in-app screens. No numbers are imported, purchased or shared.</p>
      </section>

      <section data-testid="sms-consent-language">
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-400" /> Exact consent language</h2>
        <blockquote data-testid="sms-consent-quote" className="border-l-2 border-cyan-500/50 pl-4 text-gray-300 text-sm leading-relaxed mb-4">
          “I agree to receive account and security text messages from PhonePlay (one-time passcodes, login verification and account activity alerts).
          Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help.” — followed by links to{" "}
          <Link to="/terms" className="text-cyan-400 hover:underline">Terms &amp; Rules</Link> and <Link to="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</Link>.
        </blockquote>
        <ConsentDemo t={t} id="sms-demo" />
      </section>

      <section data-testid="sms-screenshots">
        <h2 className="font-display text-xl font-bold mb-3">Opt-in screens</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <figure className="glass rounded-2xl p-3 border border-white/10">
            <img src="/sms/signup-opt-in.png" alt="PhonePlay signup form showing the optional mobile number field and the unchecked SMS consent checkbox" loading="lazy" className="rounded-xl w-full border border-white/10" />
            <figcaption className="text-xs text-gray-500 mt-2">1 · Signup form (optional phone + consent checkbox)</figcaption>
          </figure>
          <figure className="glass rounded-2xl p-3 border border-white/10">
            <img src="/sms/wallet-opt-in.png" alt="PhonePlay Wallet Phone and Two-Factor Security card showing the phone input, unchecked consent checkbox and disabled Send code button" loading="lazy" className="rounded-xl w-full border border-white/10" />
            <figcaption className="text-xs text-gray-500 mt-2">2 · Wallet › Phone &amp; Two-Factor Security (after login)</figcaption>
          </figure>
        </div>
      </section>

      <section data-testid="sms-sample">
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><MessageSquareText className="w-5 h-5 text-fuchsia-400" /> Sample message</h2>
        <div className="max-w-sm rounded-2xl rounded-bl-sm bg-white/10 border border-white/10 px-4 py-3 text-sm text-gray-200 font-mono" data-testid="sms-sample-message">{SAMPLE}</div>
        <ul className="text-xs text-gray-500 mt-3 space-y-1">
          <li>Message frequency varies (typically 1 message per login or security event).</li>
          <li>Msg &amp; data rates may apply. Reply <span className="font-mono">STOP</span> to opt out at any time, <span className="font-mono">HELP</span> for help.</li>
          <li>Mobile numbers and SMS consent are never shared with third parties for marketing or promotional purposes.</li>
        </ul>
      </section>

      <p className="text-xs text-gray-600">
        <Link to="/terms" data-testid="sms-terms-link" className="text-cyan-400 hover:underline">Terms &amp; Rules</Link> · <Link to="/privacy" data-testid="sms-privacy-link" className="text-cyan-400 hover:underline">Privacy Policy</Link> · Phone Play, LLC · A Damani Holdings company
      </p>
    </article>
  );
}
